import { API_BASE_URL as BASE } from '@/lib/apiBase';

const ME_PATH = '/auth/me';
const POSTS_PATH = '/admin/posts';
const FETCH_PATH = '/admin/fetch-url';

interface ApiError {
  error: string;
}

export interface PostSummary {
  slug: string;
  title: string;
  status: 'draft' | 'published' | 'deleted';
  date: string;
}

export interface PostDetail {
  slug: string;
  title: string;
  description: string;
  date: string;
  author: string;
  category: string;
  tags: string[];
  featured: boolean;
  status: 'draft' | 'published' | 'deleted';
  content: string;
}

interface CreatePostPayload {
  slug: string;
  title: string;
  description: string;
  date: string;
  author: string;
  category: string;
  tags: string[];
  featured: boolean;
  status: 'draft' | 'published';
  content: string;
}

interface PostsResponse {
  posts: PostSummary[];
}

async function request<T>(
  path: string,
  options: RequestInit = {},
  token?: string
): Promise<T> {
  const headers: HeadersInit = { 'Content-Type': 'application/json' };
  if (token) (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  const res = await fetch(`${BASE}${path}`, { ...options, headers });
  const body = await res.json() as T | ApiError;
  if (!res.ok) throw new Error((body as ApiError).error ?? `HTTP ${res.status}`);
  return body as T;
}

export const adminApi = {
  login(password: string, totp: string): Promise<{ token: string }> {
    return request('/auth', {
      method: 'POST',
      body: JSON.stringify({ password, totp }),
    });
  },

  checkSession(token: string): Promise<{ ok: boolean }> {
    return request(ME_PATH, { method: 'GET' }, token);
  },

  listPosts(token: string): Promise<PostsResponse> {
    return request(POSTS_PATH, { method: 'GET' }, token);
  },

  getPost(token: string, slug: string): Promise<PostDetail> {
    return request(`${POSTS_PATH}/${slug}`, { method: 'GET' }, token);
  },

  createPost(token: string, payload: CreatePostPayload): Promise<{ slug: string }> {
    return request(POSTS_PATH, { method: 'POST', body: JSON.stringify(payload) }, token);
  },

  updatePost(token: string, slug: string, patch: Partial<Omit<PostDetail, 'slug'>>): Promise<{ slug: string }> {
    return request(`${POSTS_PATH}/${slug}`, { method: 'PATCH', body: JSON.stringify(patch) }, token);
  },

  deletePost(token: string, slug: string): Promise<{ slug: string; deleted: boolean; permanent: false }> {
    return request(`${POSTS_PATH}/${slug}`, { method: 'DELETE' }, token);
  },

  permanentlyDeletePost(token: string, slug: string): Promise<{ slug: string; deleted: boolean; permanent: true }> {
    return request(`${POSTS_PATH}/${slug}?permanent=true`, { method: 'DELETE' }, token);
  },

  fetchUrl(token: string, url: string): Promise<{ text: string }> {
    return request(`${FETCH_PATH}?url=${encodeURIComponent(url)}`, { method: 'GET' }, token);
  },
};
